import HelpTooltip from '@/common/components/molecules/HelpTooltip/HelpTooltip.vue';
import { useGetV7DatasStatDetail } from '@/api-client/metav6/queries/data-stat-information-controller/data-stat-information-controller';
import { convertStatDetailFromDto } from '@/common/components/organisms/statWindow/statContents/statDetail/statDetail.utils';
import { useInternational } from '@/common/locale';
import { getQueryStatTooltipText } from '@/dashboard/features/widgets/common/domain/queryStatTimezone';
import type { QueryStatTimezoneOption } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/queryStatTimezoneOption.setup';
import { WidgetType } from '@/dashboard/shared/types/widgets.types';
import {
  computed,
  ComputedRef,
  CSSProperties,
  MaybeRefOrGetter,
  onBeforeUnmount,
  ref,
  toValue,
  watch,
} from 'vue';
import { isNil } from 'lodash-es';

export interface StatInformationProps {
  title: string;
  metrics: string[];
  queryStatTimezone?: QueryStatTimezoneOption;
  /** scatter 등 Time Period 기준 "시간 기준" 표시값(상위에서 산출). queryStatTimeLabel 우선. */
  timeReferenceLabel?: string;
  widgetType?: WidgetType;
}

export interface MetricInfo {
  id: string;
  name: string;
  description: string;
  unit: string;
}

export const useMetricInfo = (
  metricIds: ComputedRef<string[]>,
  options?: {
    enabled?: MaybeRefOrGetter<boolean>;
  },
) => {
  const params = computed(() => ({
    dataIds: metricIds.value.filter((id) => id),
  }));

  const {
    data: metricInfoList,
    isPending: isLoading,
    refetch,
  } = useGetV7DatasStatDetail(params, {
    query: {
      enabled: computed(
        () =>
          metricIds.value.filter((id) => id).length > 0 &&
          (isNil(options?.enabled) ? true : toValue(options.enabled)),
      ),
      select: (response) => response.data.data?.map(convertStatDetailFromDto),
    },
  });

  return {
    metricInfoList,
    isLoading,
    refetch,
  };
};

export const setup = (props: StatInformationProps) => {
  const { t } = useInternational();
  const { isLoading, metricInfoList } = useMetricInfo(computed(() => props.metrics));
  const queryStatTimeLabel = computed(() => {
    return (
      getQueryStatTooltipText(props.queryStatTimezone, {
        noneLabelKey: 'WORD.QUERY_SETTING_REFERENCE',
      })?.label ?? ''
    );
  });
  // 쿼리 정의 지표(reference)면 queryStatTimeLabel, 그 외(scatter Time Period 등)는 상위 산출값.
  const timeReferenceLabel = computed(
    () => queryStatTimeLabel.value || props.timeReferenceLabel || '',
  );
  const tooltipRef = ref<InstanceType<typeof HelpTooltip>>();
  const contentBodyRef = ref<HTMLDivElement>();
  const contentsTop = ref(0);
  const isWheelEventRegistered = ref(false);
  const recalculateContentStyle = computed<CSSProperties>(() => {
    const HEADER_HEIGHT = 40;
    const MARGIN = 5;
    return {
      maxHeight: `calc(100vh - ${contentsTop.value + HEADER_HEIGHT + MARGIN}px)`,
      overflowY: 'auto',
    };
  });

  const onMouseWheel = (event: WheelEvent) => {
    const targetElem = contentBodyRef.value;
    if (targetElem && targetElem.scrollHeight > targetElem.clientHeight) {
      targetElem.scrollTop += event.deltaY;
      event.stopPropagation();
      event.preventDefault();
      return true;
    }
    return false;
  };

  watch(contentBodyRef, (targetElem) => {
    const tooltipRootElem = tooltipRef.value?.$el;
    if (targetElem && tooltipRootElem) {
      const { top } = tooltipRootElem.getBoundingClientRect();
      contentsTop.value = top;
      if (!isWheelEventRegistered.value) {
        window.addEventListener('wheel', onMouseWheel, {
          capture: true,
          passive: false,
        });
        isWheelEventRegistered.value = true;
      }
    }
  });

  onBeforeUnmount(() => {
    if (isWheelEventRegistered.value) {
      window.removeEventListener('wheel', onMouseWheel, {
        capture: true,
      });
    }
  });

  return {
    tooltipRef,
    contentBodyRef,
    recalculateContentStyle,
    metricInfoList,
    isLoading,
    timeReferenceLabel,
    t,
  };
};
